import type { PrototypeRuntime, Root } from './prototypeTypes';
import type { CommunityListItem } from './communityIdeaListRenderer';

export type CommunityTab = 'recent' | 'completed';
export interface CommunityTabState { search: string; items: CommunityListItem[]; nextCursor: string | null; windowEnd?: string; loadGeneration: number; loadAbortController: AbortController | null; error: string | null; scrollTop: number; restoreScrollOnNextPaint: boolean; }
export interface CommunityMountState { runtime: PrototypeRuntime; activeTab: CommunityTab; filter: CommunityTab; tabs: Record<CommunityTab, CommunityTabState>; }
export interface CommunityReturnState { filter: CommunityTab; search: string; scrollTop?: number; }
export interface CommunityVoteCacheUpdate { viewerLiked: boolean; }

const states = new WeakMap<HTMLElement, CommunityMountState>();
const createTab = (): CommunityTabState => ({ search: '', items: [], nextCursor: null, loadGeneration: 0, loadAbortController: null, error: null, scrollTop: 0, restoreScrollOnNextPaint: false });

export function resolveCommunityMount(root: Root, origin?: HTMLElement | null): HTMLElement | null {
	if (origin?.matches('[data-community-mount]')) return origin;
	const ancestor = origin?.closest<HTMLElement>('[data-community-mount]');
	if (ancestor) return ancestor;
	const child = origin?.querySelector<HTMLElement>('[data-community-mount]');
	if (child) return child;
	return root.querySelector<HTMLElement>('#view-community.active [data-community-mount], #view-social.active .social-panel.active [data-community-mount]') ?? root.querySelector<HTMLElement>('[data-community-mount="desktop"]') ?? root.querySelector<HTMLElement>('[data-community-mount]');
}

export function getCommunityMountState(mount: HTMLElement, runtime?: PrototypeRuntime): CommunityMountState | null {
	const current = states.get(mount);
	if (current) { if (runtime) current.runtime = runtime; return current; }
	if (!runtime) return null;
	const created: CommunityMountState = { runtime, activeTab: 'completed', filter: 'completed', tabs: { recent: createTab(), completed: createTab() } };
	states.set(mount, created);
	return created;
}

export function communityHydrationKey(mount: HTMLElement): string { return `community:${mount.dataset.communityMount ?? 'default'}`; }
export function getCommunityTabState(state: CommunityMountState, tab = state.activeTab): CommunityTabState { return state.tabs[tab]; }

export function activateCommunityTab(scope: HTMLElement, tab: CommunityTab): void {
	scope.querySelectorAll<HTMLElement>('[data-community-role="filters"] .category-pill').forEach((button) => {
		const active = button.dataset.communityFilter === tab;
		button.classList.toggle('active', active); button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
	});
	scope.querySelectorAll<HTMLElement>('[data-community-role="tab-panel"]').forEach((panel) => { panel.hidden = panel.dataset.communityTabPanel !== tab; });
}

export function resetCommunityEntryState(root: Root, origin?: HTMLElement): void {
	const scope = resolveCommunityMount(root, origin); const state = scope ? getCommunityMountState(scope) : null;
	if (!scope || !state) return;
	for (const tabState of Object.values(state.tabs)) { tabState.loadAbortController?.abort(); tabState.loadGeneration += 1; }
	const completed = state.tabs.completed;
	state.activeTab = 'completed'; state.filter = 'completed';
	completed.search = ''; completed.items = []; completed.nextCursor = null; completed.windowEnd = undefined; completed.error = null; completed.scrollTop = 0; completed.restoreScrollOnNextPaint = false;
	activateCommunityTab(scope, 'completed');
	const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]'); if (input) input.value = '';
	const clear = scope.querySelector<HTMLElement>('[data-community-role="search-clear"]'); if (clear) clear.style.display = 'none';
}

export function restoreCommunityReturnState(root: Root, origin: HTMLElement | undefined, value: CommunityReturnState): void {
	const scope = resolveCommunityMount(root, origin); const state = scope ? getCommunityMountState(scope) : null;
	if (!scope || !state) return;
	state.activeTab = value.filter; state.filter = value.filter; state.tabs[value.filter].search = value.search;
	if (Number.isFinite(value.scrollTop) && (value.scrollTop ?? 0) >= 0) {
		state.tabs[value.filter].scrollTop = value.scrollTop!;
		state.tabs[value.filter].restoreScrollOnNextPaint = value.scrollTop! > 0;
	}
	activateCommunityTab(scope, value.filter);
	const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]'); if (input) input.value = value.search;
	const clear = scope.querySelector<HTMLElement>('[data-community-role="search-clear"]'); if (clear) clear.style.display = value.search ? '' : 'none';
}

/**
 * Reconciles a single proposal across the active Community mount only. The
 * source can live in the dialog, so resolving from source.closest() would
 * leave the underlying list stale after the dialog closes.
 */
export function syncCommunityIdeaVoteCache(root: Root, ideaId: string, update: CommunityVoteCacheUpdate & { selected?: boolean }): void {
	const scope = resolveCommunityMount(root); const state = scope ? getCommunityMountState(scope) : null;
	if (!scope || !state || !ideaId) return;
	for (const tabState of Object.values(state.tabs)) {
		const index = tabState.items.findIndex((idea) => idea.id === ideaId);
		if (index < 0) continue;
		const current = tabState.items[index]!;
		tabState.items[index] = {
			...current,
			viewerLiked: update.viewerLiked,
			...(update.selected ? { status: 'selected', isAdopted: true } : {}),
		};
	}
	scope.querySelectorAll<HTMLElement>('[data-idea-id]').forEach((card) => {
		if (card.dataset.ideaId !== ideaId) return;
		card.dataset.viewerLiked = String(update.viewerLiked);
		card.querySelectorAll<HTMLElement>('.vote-btn, .idea-detail-vote-btn').forEach((button) => {
			button.classList.toggle('voted', update.viewerLiked);
			if (update.selected) { button.setAttribute('disabled', ''); button.setAttribute('aria-disabled', 'true'); }
		});
		if (!update.selected) return;
		card.dataset.status = 'selected'; card.dataset.isAdopted = 'true';
		const status = card.querySelector<HTMLElement>('.community-detail-status-row .pill, .idea-footer .pill, .idea-status-vote-row .pill');
		if (status) { status.textContent = 'Selected'; status.className = 'pill pill-completed'; }
	});
}

export function captureCommunityReturnState(root: Root, origin: HTMLElement): CommunityReturnState | null {
	void root;
	const mount = origin.matches('[data-community-mount]') ? origin : origin.closest<HTMLElement>('[data-community-mount]');
	if (!mount || !isActiveCommunityMount(mount)) return null;
	const state = getCommunityMountState(mount);
	if (!state) return null;
	const tab = isCommunityTab(state.filter) ? state.filter : state.activeTab;
	const viewportScroll = typeof window !== 'undefined' ? window.scrollY : undefined;
	const scrollTop = typeof viewportScroll === 'number' && Number.isFinite(viewportScroll) && viewportScroll >= 0
		? viewportScroll
		: Math.max(0, state.tabs[tab].scrollTop);
	state.tabs[tab].scrollTop = scrollTop;
	return { filter: tab, search: (mount.querySelector<HTMLInputElement>('[data-community-role="search-input"]')?.value ?? state.tabs[tab].search).trim(), scrollTop };
}

function isActiveCommunityMount(mount: HTMLElement): boolean { return mount.closest('#view-community.active') !== null || mount.closest('#view-social.active .social-panel.active') !== null; }

export function communityReturnStateFromHistory(state: unknown): CommunityReturnState | null {
	if (!state || typeof state !== 'object') return null;
	const candidate = (state as { communityReturnState?: unknown }).communityReturnState;
	if (!candidate || typeof candidate !== 'object') return null;
	const { filter, search, scrollTop } = candidate as { filter?: unknown; search?: unknown; scrollTop?: unknown };
	if (!isCommunityTab(filter) || typeof search !== 'string') return null;
	return typeof scrollTop === 'number' && Number.isFinite(scrollTop) && scrollTop >= 0 ? { filter, search, scrollTop } : { filter, search };
}

export function isCommunityTab(value: unknown): value is CommunityTab { return value === 'recent' || value === 'completed'; }
